import { _t } from '@bark/bark-core';

export const GooglePlacesMapManager = () => {
	const { bugsnagClient } = window;
	let { google } = window;

	const state = {
		mapState: {},
		mapOptions: {},
		map: null,
		autocomplete: null,
		marker: null,
		place: null,
		errorContainer: null,
		googleMapNotReadyRetryCount: 0,
	};

	const defaultMapStateLondon = {
		center: { lat: 51.5158, lng: -0.17788 },
		zoom: 13,
	};

	const defaultMapState = {
		center: { lat: 54.816034856125775, lng: -4.530632668290737 },
		zoom: 5,
	};

	const defaultMapOptions = {
		disableDefaultUI: true,
	};

	/**
	 * initMap -- initialise the map and autocomplete
	 * @param {Object} domElements
	 * @param {Object} options
	 * @param {string} domElements.containerId
	 * @param {string} domElements.addressInputId
	 * @param {string=} domElements.errorContainerId -- optional container to show error messages in
	 * @param {string} countryCode  --  an ISO 3166-1 two letter country code.
	 * @param {object?} additionalMapOptions -- Must comply with the Google Javascript API reference https://developers.google.com/maps/documentation/javascript/reference/map#MapOptions
	 *
	 */
	const initMap = (domElements, countryCode, additionalMapOptions = {}) => {
		const { containerId, addressInputId, errorContainerId } = domElements;
		const container = document.getElementById(containerId);
		const addressInput = document.getElementById(addressInputId);
		const overallLocationContainer = document.getElementById(
			'company-location-container',
		); // Only used in an emergency to remove the location settings info container

		if (errorContainerId) {
			state.errorContainer = document.getElementById(errorContainerId);
		}

		let defaultMap = {
			...defaultMapState,
			...defaultMapOptions,
			...additionalMapOptions,
		};

		checkIfMapReady(function (mapReady) {
			if (!mapReady) {
				container.remove();
				addressInput.remove();
				if (overallLocationContainer) {
					overallLocationContainer.remove();
				}
			} else {
				google = window.google;
				const map = (state.map = new google.maps.Map(container, defaultMap));
				// Init marker
				const marker = (state.marker = new google.maps.Marker({
					map,
					anchorPoint: new google.maps.Point(0, -29),
				}));

				// Init autocomplete
				let autocompleteOptions = {
					componentRestrictions: { country: countryCode },
					fields: [
						'formatted_address',
						'address_components',
						'geometry',
						'name',
						'place_id',
					],
					origin: map.getCenter(),
					strictBounds: false,
					types: [],
				};
				const autocomplete = (state.autocomplete = new google.maps.places.Autocomplete(
					addressInput,
					autocompleteOptions,
				));
				autocomplete.bindTo('bounds', map);
				bindAutocompleteListener();
			}
		});
	};

	/**
	 * bindAutocompleteListener -- binds the 'place_changed' event to the autocomplete object
	 */
	const bindAutocompleteListener = () => {
		let { autocomplete, marker, map } = state;
		autocomplete.addListener('place_changed', () => {
			clearError();
			marker.setVisible(false);
			const place = (state.place = autocomplete.getPlace());
			if (!place.geometry || !place.geometry.location) {
				// User entered the name of a Place that was not suggested and
				// pressed the Enter key, or the Place Details request failed.
				showError(_t("seller_settings_profile:google-places.place-changed-error-message"));
				return;
			}

			// If the place has a geometry, then present it on a map.
			if (place.geometry.viewport) {
				map.fitBounds(place.geometry.viewport);
			} else {
				map.setCenter(place.geometry.location);
				map.setZoom(17);
			}

			marker.setPosition(place.geometry.location);
			marker.setVisible(true);

			// Save the state for later
			let mapState = getMapState(map);
			saveMapState(mapState);
		});
	};

	/**
	 * showError -- shows an error message in the defined error message container
	 *              if message container is undefined, and message is critical, alerts().
	 * @param {string} message -- Message to show
	 * @param {boolean} critical -- Force display of the message even if there's no message container.
	 */
	const showError = (message, critical = false) => {
		let { errorContainer } = state;
		if (errorContainer) {
			errorContainer.innerHTML = message;
			errorContainer.style.display = 'block';
		} else {
			if (critical) {
				bugsnagClient.notify(
					new Error(
						`A map error occurred, marked as critical, but there is no nice UI to show it. Alerting message: ${messsage}`,
					),
				);
				window.alert(message);
			}
		}
	};

	/**
	 * clearError -- clears error message
	 */
	const clearError = () => {
		let { errorContainer } = state;
		if (errorContainer) {
			errorContainer.style.display = 'none';
			errorContainer.innerHTML = '';
		} else {
			return false;
		}
	};

	/**
	 * setMapToState -- set the map to the state specified
	 * @param {Object} mapState -- state of the map
	 * @param {Object} mapState.center -- lat / long of the center point
	 * @param {number} mapState.center.lat -- latitude
	 * @param {number} mapState.center.lng -- longitude
	 * @param {number} mapState.zoom -- zoom level
	 * @param {Object=} additionalOptions -- additional options: https://developers.google.com/maps/documentation/javascript/reference/map#MapOptions
	 */
	const setMapToState = (mapState, additionalOptions = null) => {
		let map = state.map;
		let { center, zoom } = mapState;

		try {
			if (mapState.center) {
				map.setCenter(center);
			}

			if (mapState.zoom) {
				map.setZoom(zoom);
			}

			if (additionalOptions) {
				map.setOptions(additionalOptions);
			}
		} catch (err) {
			bugsnagClient.notify(err, {
				metaData: { 'Map state': mapState, 'Additional options': additionalOptions },
			});
		}
	};

	/**
	 * getMapState -- get the state from the map
	 * @param {Object} map -- Google Map Object
	 */
	const getMapState = (map = state.map) => {
		let mapState = {};
		mapState.zoom = map.getZoom();
		mapState.center = {};
		mapState.center.lat = map.getCenter().lat();
		mapState.center.lng = map.getCenter().lng();
		return mapState;
	};

	/**
	 * getPlaceState -- get the place, or try and get it from autocomplete
	 * @param {Object} place -- Google Place Object
	 */
	const getPlaceState = (place = state.place) => {
		if (!place && state.autocomplete) {
			// If no place, try and get it
			place = state.autocomplete.getPlace();
		}
		return place;
	};

	const setPlaceState = (place) => {
		state.place = place;
	};

	/**
	 * getMapAndPlaceState
	 * Use this interface externally.
	 */
	const getMapAndPlaceState = () => {
		let mapState = getMapState(state.map);
		let placeState = getPlaceState(state.place);
		let fullState = { ...mapState, ...placeState };

		if (!mapState || !placeState) {
			return false;
		} else {
			return fullState;
		}
	};

	const getAddressComponentsFromPlace = () => {
		if (!state?.place?.address_components) {
			return {};
		} else {
			return state.place.address_components;
		}
	};

	const setMarker = (center) => {
		let { marker } = state;
		marker.setPosition(center);
		marker.setVisible(true);
	};

	/**
	 * setMapToDefaultState -- sets the map to the default state and options
	 */
	const setMapToDefaultState = () => {
		setMapToState(defaultMapState, defaultMapOptions);
	};

	/**
	 * setBestDefaultMapViewForCountry --  gets the best initial view state depending on country (UK, US, Aus etc)
	 *                                  this function does NOT change the actual map
	 * @param {string} countryCode  --  an ISO 3166-1 two letter country code. (Except Singapore. Yay bark.)
	 */
	const getBestDefaultMapViewForCountry = (countryCode) => {
		let states = {
			us: {
				center: { lat: 38.50710655210107, lng: -100.25914459760531 },
				zoom: 4,
			},
			gb: {
				center: { lat: 54.816034856125775, lng: -4.530632668290737 },
				zoom: 5,
			},
			za: {
				center: { lat: -28.80406704879587, lng: 22.06188222605653 },
				zoom: 5,
			},
			au: {
				center: { lat: -26.45162001376248, lng: 131.23567177016037 },
				zoom: 4,
			},
			nz: {
				center: { lat: -41.899477, lng: 175.310128 },
				zoom: 5,
			},
			ca: {
				center: { lat: 58.695434, lng: -96 },
				zoom: 3,
			},
			ie: {
				center: { lat: 53.460811, lng: -7.955967 },
				zoom: 7,
			},
			sg: {
				center: { lat: 1.314005, lng: 103.84425 },
				zoom: 7,
			},
            fr: {
                center: { lat: 46.30303, lng: 1.98754 },
                zoom: 5,
            },
            de: {
                center: { lat: 51.2197, lng: 10.1952 },
                zoom: 5,
            },
            in: {
                center: { lat:  22.6132, lng: 78.1122},
                zoom: 4,
            },
			world: {
				center: { lat: 32.55799439770026, lng: -10.435636919298558 },
				zoom: 2,
			},
		};

		// Try and choose a good default state.
		if (countryCode) {
			countryCode = countryCode.toLowerCase();
			return states?.[countryCode] ?? states.world;
		}

		// Fallback
		return states.world;
	};

	const setDefaultStateValue = (stateToSet) => {
		if (stateToSet) {
			if (stateToSet.center) {
				defaultMapState.center = stateToSet.center;
			}
			if (stateToSet.zoom) {
				defaultMapState.zoom = stateToSet.zoom;
			}
		}
	};

	/**
	 * saveMapState -- save the state of the map for later use
	 * @param {Object} mapState -- state of the map
	 * @param {Object} mapState.center -- lat / long of the center point
	 * @param {number} mapState.center.lat -- latitude
	 * @param {number} mapState.center.lng -- longitude
	 * @param {number} mapState.zoom -- zoom level
	 * @param {Object=} additionalOptions -- additional options: https://developers.google.com/maps/documentation/javascript/reference/map#MapOptions
	 */
	const saveMapState = (mapState, additionalOptions = null) => {
		state.mapState = mapState;
		state.mapOptions = additionalOptions;
	};

	const getAutocompleteObject = () => {
		if (state.autocomplete) {
			return state.autocomplete;
		} else {
			return false;
		}
	};

	/**
	 * GoogleMap initialisation wait
	 * @param {function} callback
	 */
	const checkIfMapReady = (callback) => {
		if (window.google) {
			callback(true);
		} else if (state.googleMapNotReadyRetryCount > 25) {
			bugsnagClient.notify(
				new Error(
					`Waited for 5 seconds but Google Maps was still not available. Removing 'Location' section from the page.`,
				),
			);
			callback(false);
		} else {
			window.setTimeout(function () {
				state.googleMapNotReadyRetryCount++;
				checkIfMapReady(callback);
			}, 200);
		}
	};

	return {
		initMap,
		saveMapState,
		setMapToDefaultState,
		setMapToState,
		getMapAndPlaceState,
		setMarker,
		getBestDefaultMapViewForCountry,
		setDefaultStateValue,
		setPlaceState,
		getAutocompleteObject,
		getAddressComponentsFromPlace,
	};
};
